import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, InputAdornment, TextField, Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Edit, PictureAsPdf, Visibility, Assessment, Search, Add } from "@mui/icons-material";
import moment from "moment";
import {
  useAnalysis,
  useCompany,
  useNewVehicle,
  useReplacement,
} from "../../common/hooks";
import {
  getCompanyId,
} from "../../common/constants/commonFunction";
import { TruckLoader } from "./truckLoader";
import EditReportName from "../../components/Analysis-Report/editReportName";
import { handleExportToPDF } from "../../common/services/exportPowerBIPdf";
import { getCurrencyValue } from "../../common/services/number-service";
import { useNavigate } from "react-router-dom";
import { handleExportButton } from "../../common/services/exportPowerBI";
import { handleExportToCSV, handleExportToPPT } from "../../common/services/analysisExportService";
import { ConfirmAnalysisResultDialog } from "../../components/ConfirmAnalysisDialog";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TableViewIcon from "@mui/icons-material/TableView";
import GridOnIcon from "@mui/icons-material/GridOn";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

export const AnalysisResults = () => {
  const navigation = useNavigate();
  const { companyNameHeader } = useCompany();
  const [editReportModal, setEditReportModal] = useState(false);
  const [reportName, setReportName] = useState();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    getAnalysisResultApi,
    analysisResult,
    setAnalysisResult,
    loading,
    setAnalysisResultId,
  } = useAnalysis();
  const { setToastClose } = useNewVehicle();
  const userType = localStorage.getItem("type");
  const { setReplacementList } = useReplacement();


  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpenExport = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseExport = () => {
    setAnchorEl(null);
  };
  useEffect(() => {
    if (companyNameHeader) {
      const companyId = getCompanyId(userType, companyNameHeader);
      if (companyId) {
        getAnalysisResultApi({ filter: { companyId }, limit: 10000, offset: 0 });
      } else {
        setAnalysisResult([]);
      }
    }
  }, [companyNameHeader]);

  useEffect(() => {
    if (!companyNameHeader) setAnalysisResult([]);
  }, []);

  useEffect(() => {
    setReplacementList([]);
    setAnalysisResultId(null);
    setToastClose(false);
  }, []);

  const handleViewButton = (params) => {
    navigation("/suggested-plan-cost-comparison-analysis-reports", { state: { data: params } });
  };
  const handleEdit = (params) => {
    setEditReportModal(true);
    setReportName(params);
  };
  const handleClose = () => setEditReportModal(false);
  const handleCreateFleet = () => navigation("/current-fleet-details");

  const filteredResults = (analysisResult || []).filter((r) =>
    (r.reportName || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTotalSavings = (report) => {
    try {
      const data = JSON.parse(report?.reportData || "{}");
      const total = (data?.fleetMix || []).reduce((sum, row) => sum + (row.savings || 0), 0);
      return total;
    } catch {
      return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: `calc(100vh - 121px)`,
        marginTop: "60px",
        marginLeft: "calc(var(--sidebar-width) + 20px)",
        padding: "20px 20px 60px 20px",
        background: "transparent",
      }}
    >
      {/* ── Page Header ── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          px: 0.5,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
            <Assessment sx={{ color: "var(--brand-primary)", fontSize: 28 }} />
            <span
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "var(--text-primary)",
                letterSpacing: "-0.3px",
              }}
            >
              Suggested 5-Year Plan & Cost Comparison Analysis Reports
            </span>
          </Box>
          <span style={{ fontSize: 12, color: "var(--text-secondary)", marginLeft: 40 }}>
            {filteredResults.length} report{filteredResults.length !== 1 ? "s" : ""} found
          </span>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Search */}
          <TextField
            size="small"
            placeholder="Search reports…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: 16, color: "var(--text-secondary)" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 220,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                fontSize: 12,
                background: "var(--surface)",
                color: "var(--text-primary)",
                "& fieldset": { borderColor: "var(--divider)" },
                "&:hover fieldset": { borderColor: "var(--brand-primary)" },
                "&.Mui-focused fieldset": { borderColor: "var(--brand-primary)" },
              },
              "& .MuiInputBase-input": { color: "var(--text-primary)" },
            }}
          />

          {/* Create button */}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateFleet}
            sx={{
              background: "var(--brand-primary)",
              color: "white",
              fontWeight: 700,
              borderRadius: "10px",
              textTransform: "none",
              fontSize: 13,
              px: 2.5,
              py: 0.9,
              boxShadow: "0 4px 14px rgba(8,96,211,0.3)",
              "&:hover": {
                background: "var(--brand-primary-hover)",
                boxShadow: "0 6px 18px rgba(8,96,211,0.4)",
                transform: "translateY(-1px)",
              },
              transition: "all 0.2s ease",
            }}
          >
            Create Report
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 300 }}>
          <TruckLoader />
        </Box>
      ) : filteredResults.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
            gap: 2,
            color: "var(--text-secondary)",
          }}
        >
          <Assessment sx={{ fontSize: 64, opacity: 0.2 }} />
          <span style={{ fontSize: 16, fontWeight: 600 }}>No suggested 5-year plan & cost comparison analysis Reports found</span>
          <span style={{ fontSize: 12 }}>Create your first report to get started</span>
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: 2,
          }}
        >
          {filteredResults.map((report, idx) => {
            const savings = getTotalSavings(report);
            const savingsPositive = savings !== null && savings >= 0;
            return (
              <Box
                key={report.id || idx}
                sx={{
                  background: "var(--surface)",
                  border: "1px solid var(--divider)",
                  borderRadius: "16px",
                  padding: "18px 20px 16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  transition: "box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease",
                  boxShadow: "0 2px 8px rgba(8,96,211,0.05)",
                  cursor: "default",
                  "&:hover": {
                    boxShadow: "0 8px 24px rgba(8,96,211,0.13)",
                    transform: "translateY(-2px)",
                    borderColor: "var(--brand-primary)",
                  },
                }}
              >
                {/* Card Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "var(--text-primary)",
                        mb: 0.4,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={report.reportName}
                    >
                      {report.reportName || "Unnamed Report"}
                    </Box>
                    <Box sx={{ fontSize: 11, color: "var(--text-secondary)" }}>
                      {report.createdAt
                        ? moment(report.createdAt).format("MMM D, YYYY · h:mm A")
                        : "—"}
                    </Box>
                  </Box>

                  {/* Savings chip */}
                  {savings !== null && (
                    <Chip
                      label={getCurrencyValue(savings)}
                      size="small"
                      sx={{
                        ml: 1,
                        height: 24,
                        fontSize: 11,
                        fontWeight: 700,
                        background: savingsPositive
                          ? "linear-gradient(135deg, #16a34a, #22c55e)"
                          : "linear-gradient(135deg, #dc2626, #ef4444)",
                        color: "#fff",
                        border: "none",
                        boxShadow: savingsPositive
                          ? "0 2px 8px rgba(34,197,94,0.35)"
                          : "0 2px 8px rgba(239,68,68,0.35)",
                        "& .MuiChip-label": { px: 1 },
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>

                {/* Meta info */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    fontSize: 11,
                    color: "var(--text-secondary)",
                    borderTop: "1px solid var(--divider)",
                    pt: 1,
                  }}
                >
                  {report.companyName && (
                    <span>🏢 {report.companyName}</span>
                  )}
                  {report.dealerName && (
                    <span>🤝 {report.dealerName}</span>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 1, pt: 0.5, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Visibility sx={{ fontSize: "13px !important" }} />}
                    onClick={() => handleViewButton(report)}
                    sx={{
                      flex: 1,
                      background: "var(--brand-primary)",
                      color: "white",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: 11,
                      fontWeight: 600,
                      py: 0.6,
                      boxShadow: "0 2px 8px rgba(8,96,211,0.25)",
                      "&:hover": {
                        background: "var(--brand-primary-hover)",
                        boxShadow: "0 4px 12px rgba(8,96,211,0.35)",
                      },
                    }}
                  >
                    View
                  </Button>


                  <>
                    <Button
                      variant="contained"
                      endIcon={<KeyboardArrowDownIcon />}
                      onClick={handleOpenExport}
                      sx={{
                        background: "var(--brand-primary)",
                        color: "#fff",
                        borderRadius: "10px",
                        textTransform: "none",
                        fontWeight: 700,
                        minWidth: 150,
                        boxShadow: "0 4px 12px rgba(8,96,211,0.25)",
                        "&:hover": {
                          background: "var(--brand-primary-hover)",
                        },
                      }}
                    >
                      Export
                    </Button>

                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleOpenExport}
                      PaperProps={{
                        elevation: 4,
                        sx: {
                          mt: 1,
                          borderRadius: 2,
                          minWidth: 220,
                        },
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          handleCloseExport();
                          handleExportButton(report);
                        }}
                      >
                        <ListItemIcon>
                          <TableViewIcon fontSize="small" color="success" />
                        </ListItemIcon>

                        <ListItemText>Export Excel (.xlsx)</ListItemText>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleCloseExport();
                          handleExportToCSV(report);
                        }}
                      >
                        <ListItemIcon>
                          <GridOnIcon fontSize="small" color="primary" />
                        </ListItemIcon>

                        <ListItemText>Export CSV</ListItemText>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleCloseExport();
                          handleExportToPPT(report);
                        }}
                      >
                        <ListItemIcon>
                          <SlideshowIcon fontSize="small" color="warning" />
                        </ListItemIcon>

                        <ListItemText>Export PowerPoint (.pptx)</ListItemText>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          handleCloseExport();
                          handleExportToPDF(report);
                        }}
                      >
                        <ListItemIcon>
                          <PictureAsPdfIcon fontSize="small" color="error" />
                        </ListItemIcon>

                        <ListItemText>Export PDF</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>

                  <Tooltip title="Edit Report Name" arrow>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Edit sx={{ fontSize: "13px !important" }} />}
                      onClick={() => handleEdit(report)}
                      sx={{
                        flex: 1,
                        borderColor: "var(--divider)",
                        color: "var(--text-secondary)",
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: 11,
                        fontWeight: 600,
                        py: 0.6,
                        "&:hover": {
                          borderColor: "var(--brand-primary)",
                          color: "var(--brand-primary)",
                          background: "rgba(8,96,211,0.06)",
                        },
                      }}
                    >
                      Edit
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <EditReportName
        setOpen={setEditReportModal}
        open={editReportModal}
        title={"Edit Report Name"}
        data={reportName}
        handleClose={handleClose}
      />
    </Box>
  );
};
